import IhmIronHideClient from 'IhmIronHideService-coral-javascript-clients/jquery-client.js';
import { AuthenticatedRequestsClient, ServiceClient, WeblabService } from '@src/services';
import { AccessDeniedException, Exception, ValidationException } from '@src/types';
import { EnvironmentDetail, DeviceDetails, CrossFrameUtils } from '@src/utils';
import { InvalidMetricsException } from '@src/metrics';

import _ from 'lodash';

interface IDeviceDetailsType {
    deviceSerialNumber?: string;
    [x: string]: any;
}

/**
 *  This module handles the interface to the Bees Service for metrics. It should not be used directly.
 */
export class MetricsService {
    public static async publishMetric(metrics) {
        try {
            const featureEnabled = await CrossFrameUtils.sendMessageToOtherFrame({
                frame: window,
                message: {
                    method: 'isFeatureEnabled',
                    data: { feature: 'enable_putmetrics_feature', defaultValue: false },
                },
            });
            const weblabEnabled = await WeblabService.isFeatureEnabledWithCache('IRONHIDEMETRICSRATELIMIT_1332303');

            const response = featureEnabled && weblabEnabled;
            if (response === true) {
                return this.sendMetricsThroughPutMetrics(metrics);
            }
            console.error('PutMetrics API of IhmIronHide is not available for this store/stage');
            return Promise.reject('Disabled. Metrics cannot be published as PutMetrics API is not enabled for this store/stage.');
        } catch (error) {
            console.error(`Error occurred on getting isFeatureEnabled: ${error}`);
            return Promise.reject(`Error occurred on getting isFeatureEnabled: ${error}`);
        }
    }

    public static async sendMetricsThroughPutMetrics(metrics) {
        const ironHideClient = new ServiceClient('IhmIronHideClient', IhmIronHideClient, false);
        const client = ironHideClient.getClient();
        if (!_.isFunction(client) && !_.has(client, 'config')) {
            // TODO: Store Isolation: Delete _.isFunction after fully migrated off IhmJSCoralConfig
            return Promise.reject(new Exception({ message: 'Service not initialized yet' }));
        }

        /**
         * Convert "https://partition.foo.amazon.com" to "https://lab05.foo.amazon.com"
         * replace _s with -s due to DNS specification, matching LPT behaviour
         */
        const storeId = EnvironmentDetail.getStoreId();
        let endpoint = ironHideClient.getClient().config.endpoint().toLowerCase();
        endpoint = endpoint.replace('partition', storeId.toLowerCase().replace(/[^a-z0-9]/g, '-'));

        let deviceSerialNumber;
        try {
            const deviceDetailsResponse: IDeviceDetailsType = await DeviceDetails.getDeviceDetails();
            deviceSerialNumber = deviceDetailsResponse.deviceSerialNumber;
        } catch (error) {
            console.log(`Fetching DSN encountered an exception ${error}`);
            return Promise.reject(error);
        }

        const clientTime = 'clientTime=' + (Date.now() / 1000).toString();
        const deviceGroup = 'deviceGroup=' + 'ATv3';
        try {
            const options = {
                url: `${endpoint}metrics/${storeId}/Tools/${deviceSerialNumber}?${clientTime}&${deviceGroup}`,
                method: 'POST',
                headers: {},
                body: metrics,
                withCredentials: true, // Sends request with certificate for mTLS.
            };

            const response = await AuthenticatedRequestsClient.crossFrameRequestRaw(options);

            if (response.ok) {
                console.log('PutMetrics through IhmIronhide called successfully');
                return Promise.resolve(`Putmetrics through ironhide called successfully, response: ${response}`);
            } else if (response.status === 403) {
                console.warn(`User does not have access to the PutMetric API.\n
                Ignoring it so that it doesn't keep getting retried.\n
                Error: ${JSON.stringify(response.body)}`);
                return Promise.resolve('User does not have access to the PutMetric API');
            } else if (response.status === 401) {
                console.warn(`User not authenticated.\n
                Ignoring it so that it doesn't keep getting retried.\n
                Error: ${JSON.stringify(response.body)}`);
                return Promise.resolve('User is not authenticated');
            } else if (response.status === 400) {
                return Promise.reject(
                    new InvalidMetricsException({
                        message: `The following metrics data was invalid: ${JSON.stringify(metrics)}.\n
                    Got a validation exception while trying to publish metrics with following content: ${JSON.stringify(response.body)}`,
                        cause: response.body,
                    }),
                );
            } else {
                console.error(`Error while calling PutMetrics through ironhide service, \n
                response:  ${JSON.stringify(response)}`);
                return Promise.reject('Error while calling PutMetrics through ironhide service');
            }
        } catch (error) {
            return Promise.reject(`Error occurred: ${JSON.stringify(error)}`);
        }
    }
}
